const hasControlCharacters = (value: string): boolean =>
  Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
const PHONE_NUMBER = /^\+?[0-9][0-9\s().-]*$/;
const PHONE_EXTENSION =
  /^(.*?)(?:;ext=|\s*(?:ext(?:ension)?\.?|x)\s*)([0-9]{1,10})$/i;

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Returns a normalized absolute web URL, or null for malformed and active
 * content schemes such as javascript:, data:, and vbscript:.
 */
export const getSafeHttpUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed || hasControlCharacters(trimmed)) return null;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

/**
 * Builds a conservative tel: URI while preserving the common E.164 shape.
 * Display formatting is handled separately from the dial target.
 */
export const getSafeTelUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed || hasControlCharacters(trimmed)) return null;

  const extensionMatch = trimmed.match(PHONE_EXTENSION);
  const number = (extensionMatch?.[1] ?? trimmed).trim();
  const extension = extensionMatch?.[2];

  if (!PHONE_NUMBER.test(number)) return null;

  const digits = number.replace(/\D/g, "");
  if (digits.length < 3 || digits.length > 15) return null;

  const normalizedNumber = `${number.startsWith("+") ? "+" : ""}${digits}`;
  return `tel:${normalizedNumber}${extension ? `;ext=${extension}` : ""}`;
};
